import { useEffect, useState } from "react";
import { Save, ChevronDown, ChevronRight as ChevronRightIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const ORDER = ["brand", "hero", "trust", "why", "services", "founder", "gallery_section", "process", "planning_experiences", "testimonials_section", "awards", "contact", "final_cta", "footer", "seo"];

const LABELS = {
  brand: "Brand",
  hero: "Hero (text only — for media use Hero Media tab)",
  trust: "Trust strip",
  why: "Why Mostash",
  services: "Services",
  founder: "Founder (text only — for photo use Founder tab)",
  gallery_section: "Gallery section",
  process: "Process",
  planning_experiences: "Planning Experiences",
  testimonials_section: "Testimonials section",
  awards: "Awards section heading",
  contact: "Contact",
  final_cta: "Final CTA",
  footer: "Footer",
  seo: "SEO",
};

export default function AdminContent() {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState("hero");

  useEffect(() => {
    let active = true;
    api.get("/content")
      .then((res) => { if (active) setContent(res.data || {}); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const save = async (key, data) => {
    try {
      const { data: doc } = await api.put(`/content/${key}`, { data });
      setContent((c) => ({ ...c, [key]: doc.data }));
      toast.success(`${LABELS[key] || key} saved`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (loading) return <div className="text-charcoal/50">Loading…</div>;

  const orderedKeys = [...ORDER.filter((k) => content[k] !== undefined), ...Object.keys(content).filter((k) => !ORDER.includes(k))];

  return (
    <div data-testid="admin-content-page">
      <header className="mb-8">
        <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">CMS</div>
        <h1 className="font-display text-3xl md:text-4xl text-charcoal">Website content</h1>
        <p className="text-charcoal/65 font-light mt-2 max-w-xl">Edit every section of the website here. Edits are live the moment you save.</p>
      </header>

      <div className="space-y-3">
        {orderedKeys.map((key) => (
          <ContentBlock
            key={key}
            sectionKey={key}
            label={LABELS[key] || key}
            data={content[key]}
            open={openKey === key}
            onToggle={() => setOpenKey(openKey === key ? null : key)}
            onSave={(d) => save(key, d)}
          />
        ))}
      </div>
    </div>
  );
}

function ContentBlock({ sectionKey, label, data, open, onToggle, onSave }) {
  const [draft, setDraft] = useState(() => JSON.stringify(data, null, 2));
  const [error, setError] = useState(null);

  useEffect(() => {
    setDraft(JSON.stringify(data, null, 2));
    setError(null);
  }, [data, open]);

  const handleSave = () => {
    try {
      const parsed = JSON.parse(draft);
      setError(null);
      onSave(parsed);
    } catch (e) {
      setError("Invalid JSON. Please fix the syntax before saving.");
    }
  };

  return (
    <section
      data-testid={`content-section-${sectionKey}`}
      className={`bg-white border border-beige transition-colors ${open ? "border-teal-600/30" : ""}`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`content-toggle-${sectionKey}`}
        className="w-full flex items-center justify-between px-5 md:px-6 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="h-4 w-4 text-teal-600" /> : <ChevronRightIcon className="h-4 w-4 text-charcoal/50" />}
          <div>
            <div className="font-display text-lg text-charcoal">{label}</div>
            <div className="uppercase tracking-[0.2em] text-[10px] text-charcoal/45">key: {sectionKey}</div>
          </div>
        </div>
      </button>
      {open ? (
        <div className="px-5 md:px-6 pb-6">
          <p className="text-xs text-charcoal/55 mb-2 font-light">
            Edit the JSON directly. Keep field names the same — change values, add gallery items, swap image URLs, edit copy, etc.
          </p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid={`content-textarea-${sectionKey}`}
            spellCheck="false"
            rows={Math.max(12, Math.min(draft.split("\n").length + 2, 32))}
            className="w-full bg-ivory border border-beige p-4 text-[12.5px] font-mono leading-relaxed focus:border-teal-600 outline-none resize-y"
          />
          {error ? <div className="mt-2 text-rose-700 text-xs">{error}</div> : null}
          <div className="mt-4 flex items-center gap-3">
            <button type="button" onClick={handleSave} data-testid={`content-save-${sectionKey}`} className="btn-primary h-10 px-5 text-[11px]">
              <Save className="h-4 w-4" /> Save changes
            </button>
            <button
              type="button"
              onClick={() => { setDraft(JSON.stringify(data, null, 2)); setError(null); }}
              className="text-[11px] uppercase tracking-[0.22em] text-charcoal/50 hover:text-teal-600"
            >
              Reset
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
